import { NewDropdown } from '@/components/NewDropdown'
import { Table } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { Text } from '../Atoms'

export type PageSize = 15 | 30 | 50 | 100

type PageSizeSelectorProps<TData> = {
  table?: Table<TData>
  pageSize?: PageSize
  onPageSizeChange?: (pageSize: PageSize) => void
  options?: PageSize[]
}

export function PageSizeSelector<TData>(props: PageSizeSelectorProps<TData>) {
  const { t } = useTranslation('common')

  const defaultOptions: PageSize[] = [15, 30, 50, 100]
  const baseOptions = props.options ?? defaultOptions

  // Support both manual pagination (via props) and table state
  const currentPageSize =
    props.pageSize ?? props.table?.getState().pagination.pageSize ?? defaultOptions[0]

  const pageSizeOptions = Array.from(new Set([...baseOptions, currentPageSize])).sort(
    (a, b) => a - b,
  )

  const handlePageSizeChange = (value: string | number) => {
    const newPageSize = Number(value) as PageSize

    if (props.onPageSizeChange) {
      // Manual pagination mode
      props.onPageSizeChange(newPageSize)
    } else if (props.table) {
      // Table state mode (for client-side pagination)
      props.table.setPageSize(newPageSize)
    }
  }

  const dropdownOptions = pageSizeOptions.map((pageSize) => ({
    value: pageSize,
    label: pageSize.toString(),
  }))

  return (
    <div className="flex items-center space-x-3">
      <Text tone="muted" as="span" className="whitespace-nowrap">
        {t('pageSizeSelector.rowsPerPage')}
      </Text>

      <NewDropdown
        options={dropdownOptions}
        value={currentPageSize}
        onChange={handlePageSizeChange}
        selectionMode="single"
        style={{
          triggerClassName: 'w-24',
        }}
      />
    </div>
  )
}
